export interface CompanyAnalysisData {
  founded_year?: number | null
  headquarters?: string | null
  employee_count?: string | null
  is_public?: number
  ticker_symbol?: string | null
  revenue_estimate?: string | null
  industry_focus?: string | null
  key_products?: string | null
  social_media?: string | null
  report?: string | null
  status?: string
}

export interface Company {
  id: string
  name: string
  booth?: string
  description?: string
  products?: string
  services?: string
  website?: string
  logo_url?: string
  highlight?: boolean
  categories: string[]
  analysis?: CompanyAnalysisData | null
}

export interface QuestionCategory {
  id: string
  name: string
  description?: string
  icon?: string
  sort_order: number
}

export interface Question {
  id: string
  category_id: string
  question_text: string
  hint?: string
  sort_order: number
}

export interface UserNote {
  id: string
  company_id: string
  content: string
  updated_at: string
}

export interface UserAnswer {
  id: string
  company_id: string
  question_id: string
  answer: string
  rating?: number
  updated_at: string
}

export interface Recording {
  id: string
  company_id: string
  filename: string
  duration_seconds?: number
  size_bytes?: number
  created_at: string
}

export interface User {
  id: string
  email: string
  name?: string
  role?: string
  approved?: boolean
}

export interface NavTrack {
  id: string
  name: string
  description: string
  icon: string
  sort_order: number
  color: string
  stops: NavStop[]
  totalStops: number
  totalMinutes: number
  mustSeeCount: number
}

export interface NavStop {
  id: string
  track_id: string
  company_id: string
  company_name: string
  booth: string
  company_description: string
  products: string
  stop_order: number
  priority: 'must-see' | 'high' | 'medium'
  visit_duration_min: number
  notes: string
  highlight: boolean
  categories: string[]
}

export interface InviteCode {
  code: string
  label: string
  max_uses: number
  used_count: number
  active: boolean
  created_at: string
  expires_at?: string
}

export interface ChatThread {
  id: string
  title: string
  messages: ChatMessage[]
  created_at: string
  updated_at: string
}

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

export type CategorySlug =
  | 'robotics'
  | 'ai-ml'
  | 'machine-vision'
  | 'motion-control'
  | 'industrial-automation'
  | 'software-digital-twin'
  | 'safety-compliance'
  | 'b2b-services'
  | 'sensors-iot'
  | 'logistics-warehousing'
  | 'humanoid-robots'
  | 'components-connectors'
  | 'education-training'
  | 'system-integrators'
  | 'pneumatics-hydraulics'
  | 'metrology-inspection'
  | 'embedded-computing'
  | 'mechanical-components'
  | 'amr-agv'
  | 'grippers-eol'
  | 'welding-joining'
  | '3d-printing'
  | 'startups'
  | 'automotive-ev'
  | 'precision-machining'
  | 'medical-life-sciences'
  | 'energy-power'
  | 'material-handling'
  | 'testing-qc'
  | 'laser-optics'
  | 'other'

export const CATEGORY_LABELS: Record<string, string> = {
  'robotics': 'Robotics',
  'ai-ml': 'AI / Machine Learning',
  'machine-vision': 'Machine Vision',
  'motion-control': 'Motion Control',
  'industrial-automation': 'Industrial Automation',
  'software-digital-twin': 'Software / Digital Twin',
  'safety-compliance': 'Safety & Compliance',
  'b2b-services': 'B2B Services',
  'sensors-iot': 'Sensors / IoT',
  'logistics-warehousing': 'Logistics & Warehousing',
  'humanoid-robots': 'Humanoid Robots',
  'components-connectors': 'Components & Connectors',
  'education-training': 'Education & Training',
  'system-integrators': 'System Integrators',
  'pneumatics-hydraulics': 'Pneumatics & Hydraulics',
  'metrology-inspection': 'Metrology & Inspection',
  'embedded-computing': 'Embedded Computing',
  'mechanical-components': 'Mechanical Components',
  'amr-agv': 'AMR / AGV',
  'grippers-eol': 'Grippers & End Effectors',
  'welding-joining': 'Welding & Joining',
  '3d-printing': '3D Printing & Additive',
  'startups': 'Startups (2019+)',
  'automotive-ev': 'Automotive & EV',
  'precision-machining': 'Precision Machining',
  'medical-life-sciences': 'Medical & Life Sciences',
  'energy-power': 'Energy & Power',
  'material-handling': 'Material Handling',
  'testing-qc': 'Testing & QC',
  'laser-optics': 'Laser & Optics',
  'other': 'Other',
}

export const CATEGORY_BADGE_CLASS: Record<string, string> = {
  'robotics': 'badge-robotics',
  'ai-ml': 'badge-ai',
  'machine-vision': 'badge-vision',
  'motion-control': 'badge-motion',
  'industrial-automation': 'badge-automation',
  'software-digital-twin': 'badge-software',
  'safety-compliance': 'badge-safety',
  'b2b-services': 'badge-b2b',
  'sensors-iot': 'badge-sensors',
  'logistics-warehousing': 'badge-logistics',
  'humanoid-robots': 'badge-humanoid',
  'components-connectors': 'badge-components',
  'education-training': 'badge-education',
  'system-integrators': 'badge-integrators',
  'pneumatics-hydraulics': 'badge-pneumatics',
  'metrology-inspection': 'badge-metrology',
  'embedded-computing': 'badge-computing',
  'mechanical-components': 'badge-mechanical',
  'amr-agv': 'badge-amr',
  'grippers-eol': 'badge-grippers',
  'welding-joining': 'badge-welding',
  '3d-printing': 'badge-3dprint',
  'startups': 'badge-startups',
  'automotive-ev': 'badge-automotive',
  'precision-machining': 'badge-machining',
  'medical-life-sciences': 'badge-medical',
  'energy-power': 'badge-energy',
  'material-handling': 'badge-material',
  'testing-qc': 'badge-testing',
  'laser-optics': 'badge-laser',
  'other': 'badge-other',
}
